import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Search, X } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'mariage', label: 'Mariage' },
  { value: 'barmitzvah', label: 'Bar/Bat Mitzvah' },
  { value: 'brit_mila', label: 'Brit Mila' },
  { value: 'coupe_cheveux', label: 'Coupe de Cheveux' },
  { value: 'henne', label: 'Henné' },
  { value: 'autre', label: 'Autre' }
];

// Categories will be loaded from DB

const PrestationsList = () => {
  const [prestations, setPrestations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Filtres de recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [prestationTypeFilter, setPrestationTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    prestation_type: 'autre',
    event_types: [],
    price_ht: 0,
    cost: 0,
    display_in_questionnaire: true,
    is_recommended: false,
    is_base_package: false,
    location: '',
    auto_select_rules: {
      enabled: false,
      event_types: [],
      min_invites: '',
      max_invites: '',
      requires_dates: [],
      requires_location: '',
      requires_has_henne: null,
      requires_has_vin_honneur: null,
      priority: 0
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadPrestations(), loadCategories()]);
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/prestation-categories`);
      setCategories(res.data);
      // Set default category if empty
      if (res.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: res.data[0].name.toLowerCase() }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPrestations = async () => {
    try {
      const res = await axios.get(`${API}/prestations`);
      // Normalize event_types to always be an array
      const normalizedPrestations = res.data.map(prestation => ({
        ...prestation,
        event_types: Array.isArray(prestation.event_types) ? prestation.event_types : []
      }));
      setPrestations(normalizedPrestations);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.event_types.length === 0) {
      toast.error('Sélectionnez au moins un type d\'événement');
      return;
    }
    
    try {
      // Préparer les données à envoyer
      const dataToSend = {
        ...formData,
        // S'assurer que auto_select_rules est null si disabled, sinon envoyer l'objet complet
        auto_select_rules: formData.auto_select_rules?.enabled ? formData.auto_select_rules : null
      };
      
      if (editingItem) {
        await axios.put(`${API}/prestations/${editingItem.id}`, dataToSend, { withCredentials: true });
        toast.success('Prestation mise à jour');
      } else {
        await axios.post(`${API}/prestations`, dataToSend, { withCredentials: true });
        toast.success('Prestation créée');
      }
      loadPrestations();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving prestation:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    try {
      await axios.delete(`${API}/prestations/${id}`, { withCredentials: true });
      toast.success('Prestation supprimée');
      loadPrestations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      const defaultRules = {
        enabled: false,
        event_types: [],
        min_invites: '',
        max_invites: '',
        requires_dates: [],
        requires_location: '',
        requires_has_henne: null,
        requires_has_vin_honneur: null,
        priority: 0
      };
      
      setFormData({
        name: item.name,
        description: item.description || '',
        category: item.category,
        prestation_type: item.prestation_type || 'autre',
        event_types: item.event_types || [],
        price_ht: item.price_ht,
        cost: item.cost || 0,
        display_in_questionnaire: item.display_in_questionnaire !== false,
        is_recommended: item.is_recommended || false,
        is_base_package: item.is_base_package || false,
        location: item.location || '',
        auto_select_rules: item.auto_select_rules ? {
          enabled: item.auto_select_rules.enabled || false,
          event_types: item.auto_select_rules.event_types || [],
          min_invites: item.auto_select_rules.min_invites || '',
          max_invites: item.auto_select_rules.max_invites || '',
          requires_dates: item.auto_select_rules.requires_dates || [],
          requires_location: item.auto_select_rules.requires_location || '',
          requires_has_henne: item.auto_select_rules.requires_has_henne ?? null,
          requires_has_vin_honneur: item.auto_select_rules.requires_has_vin_honneur ?? null,
          priority: item.auto_select_rules.priority || 0
        } : defaultRules
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: 'principale',
      prestation_type: 'autre',
      event_types: [],
      price_ht: 0,
      cost: 0,
      display_in_questionnaire: true,
      is_recommended: false,
      is_base_package: false,
      location: '',
      auto_select_rules: {
        enabled: false,
        event_types: [],
        min_invites: '',
        max_invites: '',
        requires_dates: [],
        requires_location: '',
        requires_has_henne: null,
        requires_has_vin_honneur: null,
        priority: 0
      }
    });
  };

  const handleEventTypeToggle = (eventType) => {
    setFormData(prev => ({
      ...prev,
      event_types: prev.event_types.includes(eventType)
        ? prev.event_types.filter(t => t !== eventType)
        : [...prev.event_types, eventType]
    }));
  };

  const selectAllEventTypes = () => {
    setFormData(prev => ({
      ...prev,
      event_types: EVENT_TYPES.map(et => et.value)
    }));
  };

  const getCategoryBadge = (category) => {
    const cat = categories.find(c => c.name.toLowerCase() === category.toLowerCase());
    if (cat) {
      return { label: `${cat.emoji} ${cat.name}`, color: cat.color };
    }
    return { label: category, color: 'bg-stone-100 text-stone-800' };
  };

  const getEventTypesDisplay = (eventTypes = []) => {
    // Handle null, undefined, or ensure it's an array
    const types = Array.isArray(eventTypes) ? eventTypes : [];
    if (types.length === 6) return 'Tous';
    if (types.length === 0) return 'Aucun';
    return EVENT_TYPES
      .filter(et => types.includes(et.value))
      .map(et => et.label)
      .join(', ');
  };

  // Filtrer les prestations selon les critères de recherche
  const filteredPrestations = prestations.filter(prestation => {
    // Filtre par terme de recherche (nom)
    if (searchTerm && !prestation.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all' && prestation.category !== categoryFilter) {
      return false;
    }

    // Filtre par type d'événement
    if (eventTypeFilter !== 'all') {
      const eventTypes = Array.isArray(prestation.event_types) ? prestation.event_types : [];
      if (prestation.event_type === 'all') {
        // Si event_type est 'all', inclure toutes les prestations sauf si on filtre spécifiquement
      } else if (!eventTypes.includes(eventTypeFilter) && prestation.event_type !== eventTypeFilter) {
        return false;
      }
    }

    // Filtre par type de prestation
    if (prestationTypeFilter !== 'all') {
      if (prestationTypeFilter === 'autre' && prestation.prestation_type && prestation.prestation_type !== 'autre') {
        return false;
      }
      if (prestationTypeFilter !== 'autre' && prestation.prestation_type !== prestationTypeFilter) {
        return false;
      }
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      if (statusFilter === 'visible' && prestation.display_in_questionnaire === false) {
        return false;
      }
      if (statusFilter === 'masque' && prestation.display_in_questionnaire !== false) {
        return false;
      }
      if (statusFilter === 'inactif' && prestation.is_active !== false) {
        return false;
      }
    }

    return true;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setEventTypeFilter('all');
    setPrestationTypeFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || eventTypeFilter !== 'all' || prestationTypeFilter !== 'all' || statusFilter !== 'all';

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Prestations & Options</h1>
          <p className="text-stone-600 mt-1">Gérer les prestations affichées dans le questionnaire</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="mr-2 h-4 w-4" /> Nouvelle Prestation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Modifier la Prestation' : 'Nouvelle Prestation'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nom de la prestation *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Forfait Vidéo Mariage"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Description détaillée de la prestation"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Catégorie *</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.name.toLowerCase()}>
                          {cat.emoji} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Prix HT (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_ht}
                    onChange={(e) => setFormData({ ...formData, price_ht: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Coût prestataire (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Types d'événements concernés *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={selectAllEventTypes}
                  >
                    Tout sélectionner
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 border rounded-lg bg-stone-50">
                  {EVENT_TYPES.map(eventType => (
                    <div key={eventType.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`event-${eventType.value}`}
                        checked={formData.event_types.includes(eventType.value)}
                        onCheckedChange={() => handleEventTypeToggle(eventType.value)}
                      />
                      <label
                        htmlFor={`event-${eventType.value}`}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {eventType.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de prestation</Label>
                  <Select value={formData.prestation_type} onValueChange={(val) => setFormData({ ...formData, prestation_type: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photo">📸 Photo</SelectItem>
                      <SelectItem value="video">🎬 Vidéo</SelectItem>
                      <SelectItem value="magnets">🧲 Magnets</SelectItem>
                      <SelectItem value="option">⚙️ Option</SelectItem>
                      <SelectItem value="autre">📦 Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">Organisation dans le récapitulatif</p>
                </div>

                <div>
                  <Label>Lieu / Moment</Label>
                  <Select value={formData.location || 'all'} onValueChange={(val) => setFormData({ ...formData, location: val === 'all' ? '' : val })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tous" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">🌍 Tous les lieux</SelectItem>
                      <SelectItem value="mairie">🏛️ Mairie</SelectItem>
                      <SelectItem value="henne">💅 Henné</SelectItem>
                      <SelectItem value="synagogue">🕍 Synagogue</SelectItem>
                      <SelectItem value="reception">🎉 Réception</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">Pour ordre chronologique</p>
                </div>
              </div>

              <div className="space-y-3 p-4 border rounded-lg bg-stone-50">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="display-questionnaire"
                    checked={formData.display_in_questionnaire}
                    onCheckedChange={(checked) => setFormData({ ...formData, display_in_questionnaire: checked })}
                  />
                  <label htmlFor="display-questionnaire" className="text-sm font-medium leading-none cursor-pointer">
                    Afficher dans le questionnaire public
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-recommended"
                    checked={formData.is_recommended}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_recommended: checked })}
                  />
                  <label htmlFor="is-recommended" className="text-sm font-medium leading-none cursor-pointer">
                    ⭐ Prestation recommandée (option supplémentaire suggérée)
                  </label>
                </div>
                </div>

              {/* Règles de précochage automatique */}
              <div className="space-y-4 p-4 border-2 border-amber-200 rounded-lg bg-amber-50">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">🔧 Règles de précochage automatique</Label>
                  <Checkbox
                    id="auto-select-enabled"
                    checked={formData.auto_select_rules?.enabled || false}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      auto_select_rules: { 
                        ...(formData.auto_select_rules || {
                          enabled: false,
                          event_types: [],
                          min_invites: '',
                          max_invites: '',
                          requires_dates: [],
                          requires_location: '',
                          requires_has_henne: null,
                          requires_has_vin_honneur: null,
                          priority: 0
                        }), 
                        enabled: checked 
                      }
                    })}
                  />
                </div>

                {(formData.auto_select_rules?.enabled || false) && (
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label className="text-sm mb-2 block">Types d'événements concernés</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {EVENT_TYPES.map(eventType => (
                          <div key={eventType.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`auto-${eventType.value}`}
                              checked={(formData.auto_select_rules?.event_types || []).includes(eventType.value)}
                              onCheckedChange={(checked) => {
                                const currentRules = formData.auto_select_rules || {
                                  enabled: false,
                                  event_types: [],
                                  min_invites: '',
                                  max_invites: '',
                                  requires_dates: [],
                                  requires_location: '',
                                  requires_has_henne: null,
                                  requires_has_vin_honneur: null,
                                  priority: 0
                                };
                                const newTypes = checked
                                  ? [...(currentRules.event_types || []), eventType.value]
                                  : (currentRules.event_types || []).filter(t => t !== eventType.value);
                                setFormData({
                                  ...formData,
                                  auto_select_rules: { ...currentRules, event_types: newTypes }
                                });
                              }}
                            />
                            <label htmlFor={`auto-${eventType.value}`} className="text-sm cursor-pointer">
                              {eventType.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Nombre d'invités minimum</Label>
                        <Select
                          value={formData.auto_select_rules?.min_invites || 'none'}
                          onValueChange={(val) => {
                            const currentRules = formData.auto_select_rules || {
                              enabled: false,
                              event_types: [],
                              min_invites: '',
                              max_invites: '',
                              requires_dates: [],
                              requires_location: '',
                              requires_has_henne: null,
                              requires_has_vin_honneur: null,
                              priority: 0
                            };
                            setFormData({
                              ...formData,
                              auto_select_rules: { ...currentRules, min_invites: val === 'none' ? null : val }
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Aucun" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun</SelectItem>
                            <SelectItem value="<150">Moins de 150</SelectItem>
                            <SelectItem value="150-250">150-250</SelectItem>
                            <SelectItem value=">250">Plus de 250</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm">Nombre d'invités maximum</Label>
                        <Select
                          value={formData.auto_select_rules?.max_invites || 'none'}
                          onValueChange={(val) => {
                            const currentRules = formData.auto_select_rules || {
                              enabled: false,
                              event_types: [],
                              min_invites: '',
                              max_invites: '',
                              requires_dates: [],
                              requires_location: '',
                              requires_has_henne: null,
                              requires_has_vin_honneur: null,
                              priority: 0
                            };
                            setFormData({
                              ...formData,
                              auto_select_rules: { ...currentRules, max_invites: val === 'none' ? null : val }
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Aucun" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun</SelectItem>
                            <SelectItem value="<150">Moins de 150</SelectItem>
                            <SelectItem value="150-250">150-250</SelectItem>
                            <SelectItem value=">250">Plus de 250</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm mb-2 block">Lieu requis</Label>
                      <Select
                        value={formData.auto_select_rules?.requires_location || 'none'}
                        onValueChange={(val) => {
                          const currentRules = formData.auto_select_rules || {
                            enabled: false,
                            event_types: [],
                            min_invites: '',
                            max_invites: '',
                            requires_dates: [],
                            requires_location: '',
                            requires_has_henne: null,
                            requires_has_vin_honneur: null,
                            priority: 0
                          };
                          setFormData({
                            ...formData,
                            auto_select_rules: { ...currentRules, requires_location: val === 'none' ? null : val }
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Aucun lieu requis" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun lieu requis</SelectItem>
                          <SelectItem value="mairie">Mairie</SelectItem>
                          <SelectItem value="henne">Henné</SelectItem>
                          <SelectItem value="synagogue">Synagogue</SelectItem>
                          <SelectItem value="reception">Réception</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm mb-2 block">Henné requis</Label>
                        <Select
                          value={formData.auto_select_rules?.requires_has_henne === null ? 'none' : (formData.auto_select_rules?.requires_has_henne ? 'true' : 'false')}
                          onValueChange={(val) => {
                            const currentRules = formData.auto_select_rules || {
                              enabled: false,
                              event_types: [],
                              min_invites: '',
                              max_invites: '',
                              requires_dates: [],
                              requires_location: '',
                              requires_has_henne: null,
                              requires_has_vin_honneur: null,
                              priority: 0
                            };
                            setFormData({
                              ...formData,
                              auto_select_rules: {
                                ...currentRules,
                                requires_has_henne: val === 'none' ? null : val === 'true'
                              }
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Indifférent" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Indifférent</SelectItem>
                            <SelectItem value="true">Oui (requis)</SelectItem>
                            <SelectItem value="false">Non (pas de henné)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm mb-2 block">Vin d'honneur requis</Label>
                        <Select
                          value={formData.auto_select_rules?.requires_has_vin_honneur === null ? 'none' : (formData.auto_select_rules?.requires_has_vin_honneur ? 'true' : 'false')}
                          onValueChange={(val) => {
                            const currentRules = formData.auto_select_rules || {
                              enabled: false,
                              event_types: [],
                              min_invites: '',
                              max_invites: '',
                              requires_dates: [],
                              requires_location: '',
                              requires_has_henne: null,
                              requires_has_vin_honneur: null,
                              priority: 0
                            };
                            setFormData({
                              ...formData,
                              auto_select_rules: {
                                ...currentRules,
                                requires_has_vin_honneur: val === 'none' ? null : val === 'true'
                              }
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Indifférent" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Indifférent</SelectItem>
                            <SelectItem value="true">Oui (requis)</SelectItem>
                            <SelectItem value="false">Non (pas de vin d'honneur)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm mb-2 block">Priorité</Label>
                      <Input
                        type="number"
                        value={formData.auto_select_rules?.priority || 0}
                        onChange={(e) => {
                          const currentRules = formData.auto_select_rules || {
                            enabled: false,
                            event_types: [],
                            min_invites: '',
                            max_invites: '',
                            requires_dates: [],
                            requires_location: '',
                            requires_has_henne: null,
                            requires_has_vin_honneur: null,
                            priority: 0
                          };
                          setFormData({
                            ...formData,
                            auto_select_rules: {
                              ...currentRules,
                              priority: parseInt(e.target.value) || 0
                            }
                          });
                        }}
                        placeholder="0"
                      />
                      <p className="text-xs text-stone-500 mt-1">Plus élevé = sélectionné en premier si plusieurs règles matchent</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                  {editingItem ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtres de recherche */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Barre de recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filtres */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filtre par catégorie */}
              <div>
                <Label className="text-sm mb-2 block">Catégorie</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les catégories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name.toLowerCase()}>
                        {cat.emoji} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtre par type d'événement */}
              <div>
                <Label className="text-sm mb-2 block">Type d'événement</Label>
                <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les événements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les événements</SelectItem>
                    {EVENT_TYPES.map(et => (
                      <SelectItem key={et.value} value={et.value}>
                        {et.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtre par type de prestation */}
              <div>
                <Label className="text-sm mb-2 block">Type de prestation</Label>
                <Select value={prestationTypeFilter} onValueChange={setPrestationTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="photo">📸 Photo</SelectItem>
                    <SelectItem value="video">🎬 Vidéo</SelectItem>
                    <SelectItem value="magnets">🧲 Magnets</SelectItem>
                    <SelectItem value="option">⚙️ Option</SelectItem>
                    <SelectItem value="autre">📦 Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtre par statut */}
              <div>
                <Label className="text-sm mb-2 block">Statut</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="visible">✓ Visible</SelectItem>
                    <SelectItem value="masque">✗ Masqué</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bouton pour réinitialiser les filtres */}
            {hasActiveFilters && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Réinitialiser les filtres
                </Button>
              </div>
            )}

            {/* Compteur de résultats */}
            <div className="text-sm text-stone-600">
              {filteredPrestations.length} prestation{filteredPrestations.length > 1 ? 's' : ''} trouvée{filteredPrestations.length > 1 ? 's' : ''}
              {hasActiveFilters && ` (sur ${prestations.length} au total)`}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>Événements</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Prix HT</TableHead>
                <TableHead>Coût</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPrestations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                    {hasActiveFilters ? 'Aucune prestation ne correspond aux filtres sélectionnés' : 'Aucune prestation'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrestations.map((prestation) => {
                const categoryBadge = getCategoryBadge(prestation.category);
                return (
                  <TableRow key={prestation.id}>
                    <TableCell className="font-medium">{prestation.name}</TableCell>
                    <TableCell>
                      <Badge className={categoryBadge.color}>{categoryBadge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-stone-600">
                      {getEventTypesDisplay(prestation.event_types)}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-stone-100 text-stone-600">
                        {prestation.prestation_type === 'photo' && '📸 Photo'}
                        {prestation.prestation_type === 'video' && '🎬 Vidéo'}
                        {prestation.prestation_type === 'magnets' && '🧲 Magnets'}
                        {prestation.prestation_type === 'option' && '⚙️ Option'}
                        {(!prestation.prestation_type || prestation.prestation_type === 'autre') && '📦 Autre'}
                      </Badge>
                    </TableCell>
                    <TableCell>{prestation.price_ht.toFixed(2)}€</TableCell>
                    <TableCell>{prestation.cost?.toFixed(2) || 0}€</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {prestation.display_in_questionnaire !== false && (
                          <Badge className="bg-green-100 text-green-800">✓ Visible</Badge>
                        )}
                        {prestation.is_recommended && (
                          <Badge className="bg-amber-100 text-amber-800">⭐ Recommandé</Badge>
                        )}
                        {prestation.display_in_questionnaire === false && (
                          <Badge className="bg-stone-100 text-stone-600">✗ Masqué</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openDialog(prestation)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(prestation.id)} className="text-rose-600 hover:text-rose-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrestationsList;